/**
 * Help Data — single source of truth for the in-app help drawer.
 *
 * One section per surface a person can reach from the nav, in nav order,
 * plus a short FAQ. Written against what the product does today, not what a
 * plan said it would do: the previous edition described "Routines", task
 * types and a ledger with a cycle-time figure, none of which a person can
 * find on a screen. Help that describes a product you cannot see teaches
 * someone to distrust the help.
 *
 * Rule of the file: if a sentence here cannot be checked against a screen,
 * cut it.
 */

import type { IconName } from "../icons/Icon";

export interface HelpTopic {
  id: string;
  title: string;
  /** Body is an HTML string, rendered via dangerouslySetInnerHTML. */
  body: string;
}

export interface HelpSection {
  id: string;
  title: string;
  icon: IconName;
  topics: HelpTopic[];
}

const p = (html: string): string => `<p style="margin-top:8px;">${html}</p>`;

export const helpSections: HelpSection[] = [
  {
    id: "app",
    title: "The app",
    icon: "sparkle",
    topics: [
      {
        id: "using-the-chat",
        title: "Using the chat",
        body: `
          ${p("Open <strong>/app</strong> on your phone or laptop. The <strong>Thread</strong> is where you ask for work and where it comes back; <strong>Queue</strong> holds the decisions that are yours; <strong>Reports</strong> lists what you asked for.")}
          ${p("<strong>Ask for a job.</strong> Tap a suggestion above the box — it fills the box, and nothing is sent until you press Send — or type it in your own words: <em>\"sales pack for Wednesday\"</em>, <em>\"is case KYC2026061000001 ready\"</em>, <em>\"audit report for the marketing check this month\"</em>. Days can be written as you say them: yesterday, Wednesday, 23 Sep, the 26th of August.")}
          ${p("<strong>Ask a question.</strong> <em>\"Which vendor was worst this week?\"</em>, <em>\"How did August compare with July?\"</em>, <em>\"What's waiting on me?\"</em> The answer comes from your own records, in English or Arabic, with its sources. It can offer to run a job for you, as a button; nothing runs until you tap it.")}
          ${p("<strong>Check an ad.</strong> Attach a draft (.txt, .md or .docx) or paste the copy, and say \"check this\".")}
          ${p("<strong>New topic</strong> under the box starts a fresh conversation, so a new question isn't read as a follow-up to the last one. If it can't tell what you meant, it offers the nearest jobs as buttons — one tap runs one.")}
        `,
      },
      {
        id: "checking-a-result",
        title: "Checking a result",
        body: `
          ${p("Every result is a card: a headline, four figures, and what the code found — the phrase that was flagged, the channel that moved and by how much against its usual level, the facts of a case. Every figure on the card was <strong>computed by code</strong> from the records; the words around them were drafted by a model, and every number in them was put there by code, not typed by the model.")}
          ${p("Tap <strong>How this was made</strong> to see, in five steps: what was read, what was calculated, which rules applied and where each comes from, what the model wrote, and what was checked before you saw it.")}
          ${p("If a run stopped before drafting, the line says what it needed, that it wasn't something you did, and who has it now. If a draft failed its checks, it is never shown as the answer: its figures still show, and the draft goes to a person to read.")}
          ${p("If something is wrong, open the run and press <strong>It was wrong</strong>. It is counted at once, and any standing approval for that decision stops until a person turns it back on.")}
        `,
      },
      {
        id: "approving",
        title: "Approving a decision",
        body: `
          ${p("When something waits on you, a bar sits above the box: <em>\"3 decisions waiting\"</em>. Tap <strong>Review</strong> and the decision takes the screen, one at a time, with ‹ › to move between them.")}
          ${p("Each card says why it is waiting, shows what the code found, and says what Approve will do — for a pack, email it to its recipients. Where earlier decisions help, it says <strong>We suggest</strong> and why (\"Matches 3 earlier approvals\"); nothing is chosen for you — tap <strong>Use the suggestion</strong> or choose yourself.")}
          ${p("Choose <strong>Approve</strong>, <strong>Reject</strong> or <strong>Ask for more</strong>, write at least a sentence saying why, and press Record. Your reason is kept for the audit trail and the next reviewer. A decision cannot be undone; a second opinion is a new run.")}
          ${p("<strong>Similar decisions</strong> — same agent, same figures — can be decided together: tap <strong>Resolve all</strong>, and each one is recorded under your name with the one reason.")}
          ${p("<strong>Decisions by email.</strong> By default you get one email a day, at 08:00, listing what waits on you. An ad or other decision that can't be undone also emails you once if it has waited two hours. To change this, open the menu under your initials (the sidebar on a wide screen) and set <strong>Email me about decisions</strong> to Once a day, As each one comes, or Never.")}
          ${p("<strong>A code to approve.</strong> The monthly board pack and KYC cases need a six-digit code, emailed to you, before they can be approved; it works once, for five minutes. Rejecting never needs one. In the demo the code is shown on the card as well.")}
          ${p("<strong>A draft that failed its checks</strong> is not approved by default: reject it, run it again, or read it and release it anyway, knowingly.")}
          ${p("Decisions raised by a law or regulation are the Compliance reviewer's, whoever asked. Everything else is decided by the role its agent names.")}
        `,
      },
      {
        id: "creating-an-agent",
        title: "Getting a new agent, or access to one",
        body: `
          ${p("<strong>Start in the chat.</strong> Tap <strong>Browse agents</strong> under the box, or say what you need in your own words, for example \"vendors with the most tickets this week\". If an agent already does that work and your role may use it, it runs and the result appears in the thread.")}
          ${p("If an agent does it but <strong>isn't open to your role</strong>, the chat says so in one line, naming the agent, your role, and that a <strong>Supervisor</strong> can open it for you. Nothing runs.")}
          ${p("<strong>Pick a template, or describe it.</strong> In Browse agents, choose an area and tap <strong>Use this</strong>; or describe the report you want (\"build a weekly report of redemptions by channel\"). Either way you get a card that says, in plain words, what it will do and what it may access: what it reads, how sensitive that is, who sees it, what it may do, and how often it runs. Everything on the card is written by code from the data it may read; it reports totals, never individuals.")}
          ${p("<strong>Ask for access.</strong> Tap <strong>Ask for access</strong> and your Supervisor gets the same card. The Supervisor approves what it may read and who sees it, not its figures: those come from code, every run.")}
          ${p("<strong>It runs as a Trial.</strong> Once approved it is yours: ask for it by name, or tap it among the suggestions. It first runs on five past periods so it has history, and its checks are scored on every run: the same inputs give the same figures, the rules give the same verdicts, its name reaches it, and its words carry only computed figures. When they pass, it stops being a Trial and is Active. If one fails, it is put on hold and the thread says why.")}
          ${p("<strong>Changing it.</strong> Tap <strong>Change something</strong> on its card: the period, the grouping, the top 5 or 10, the comparison, how often. Changing what it reports doesn't need approval again; changing what it reads or who sees it does. To let others see it, tap <strong>Share with…</strong> and pick the roles, and your Supervisor is asked.")}
          ${p("<strong>Reuse what a colleague built.</strong> An Active agent can be offered to others with <strong>Publish to the library</strong>, once a Supervisor approves. It then appears in Browse agents under <strong>Built by your team</strong>, with who built it. Tap <strong>Use this</strong> and you get your own copy; if the Supervisor already approved it for your role, it starts as your Trial without a new request.")}
          ${p("A brand-new kind of data or calculation, one no agent reads yet, is added by the WorkForceOS team, because every figure comes from code, never from the model.")}
          ${p("<strong>For Supervisors: composed agents.</strong> Requests, shares and library requests come to your thread and your Queue as cards; approve or say no with a sentence of why. In the console, <strong>Template studio</strong> lists them under <strong>Composed agents</strong>: who asked for each, its status, what it may access, its latest checks and its runs. <strong>Retire</strong>, with a reason, stops one for good: its schedule stops, it leaves the library, and the person who asked is told why.")}
          ${p("<strong>For Supervisors: opening or changing an agent.</strong> In the console, open <strong>Template studio</strong> and pick the agent. Press <strong>New draft</strong>: a draft is copied from the version running now. Tick <strong>who may ask for it</strong> and save, then press <strong>Run evals</strong>.")}
          ${p("Four checks run as code: the same inputs give the same figures; past runs reach the same verdicts under today's rules (runs the rules service never answered for are left out); requests reach this agent and no other; drafts carry only computed figures. <strong>Approve</strong> stays off, with the reason beside it, until all four pass. An agent with too few runs says \"Not enough history yet\"; runs are made by its schedule or when someone asks. Approving retires the version before it, and the change reaches people's chats straight away.")}
          ${p("Each decision an agent makes has its own policy there too: whether earlier approvals can be applied without asking (never for the board pack or a KYC case), how many must match and on which figures, and whether approving needs a code.")}
        `,
      },
    ],
  },
  {
    id: "welcome",
    title: "Welcome",
    icon: "sparkle",
    topics: [
      {
        id: "what-this-is",
        title: "What this is",
        body: `
          <p>A roster of agents doing the routine work of named roles, on a schedule. Each agent serves one role and one task — the daily sales pack, the weekly executive pack, checking a campaign asset, judging a KYC case — and every run it makes is a record you can open.</p>
          ${p("You ask in the chat, in your own words or with a suggestion under the box. Code decides which agent does the work; the agent's figures come from code, never from the model; and a person decides only at the moments the agent could not.")}
        `,
      },
      {
        id: "two-minds",
        title: "Two minds",
        body: `
          <p>Every agent is two things working in a fixed order.</p>
          ${p("<strong>The deterministic mind</strong> is code. It loads the data, computes every figure, resolves the rules that apply on the date being judged, and decides whether the run may go on. Every number you see anywhere came from it.")}
          ${p("<strong>The model</strong> only writes prose, and only after the first mind has said it may. It is shown the <em>names</em> of the figures, never their values, and every number in what it writes is substituted from the calculation afterwards. A figure it made up fails the run.")}
          ${p("So a wrong number is impossible by construction, and a wrong sentence is caught by validators before anyone sees it.")}
        `,
      },
      {
        id: "roles",
        title: "Who sees what",
        body: `
          <ul>
            <li><strong>Supervisor</strong> — owns the roster: how much rope each decision has, who signs it, who receives each report, the organisation and the ledger's baselines.</li>
            <li><strong>Team member</strong> — works their queue; the roster and the regulation lookup are context for it.</li>
            <li><strong>Compliance reviewer</strong> — decides whatever a law or regulation requires a person for, and reads the evidence behind an ad.</li>
            <li><strong>Agent handler</strong> — maintains the agents: schedules, failures, and what a schedule could not do. Holds no business decisions, so no queue.</li>
          </ul>
          ${p("Each role's navigation shows only the surfaces that role uses. If something is missing from your menu, it is not yours.")}
        `,
      },
    ],
  },
  {
    id: "home",
    title: "Home",
    icon: "dashboard",
    topics: [
      {
        id: "home-reads",
        title: "What Home tells you",
        body: `
          <p>The one thing that needs you, then what the agents did today, counted from the run records. Every figure on the page is a count of rows; there are no targets and no projections.</p>
          ${p("For a supervisor, team member or reviewer it lists the decisions waiting on <em>you</em> and says when a decision is open but someone else's to sign. For an agent handler it lists what broke instead — a failed run holds no business decision, so it is theirs, not the queue's.")}
        `,
      },
    ],
  },
  {
    id: "queue",
    title: "Decisions",
    icon: "actions",
    topics: [
      {
        id: "what-a-fork-is",
        title: "What a decision is",
        body: `
          <p>A decision is the one place an agent interrupts a person. It opens when a rule stopped the run, when something the rules need could not be found, when the draft failed a check, or when its agent holds the work for sign-off.</p>
          ${p("<strong>Waiting on you</strong> comes first; <strong>Waiting on others</strong> is below it, so you can see what is held elsewhere. Each card leads with the rule's own sentence and names the record — which campaign asset, which case, which day.")}
        `,
      },
      {
        id: "who-decides",
        title: "Who may decide",
        body: `
          <p>The card says who decides before you type anything. A decision raised by a <strong>law, regulation or Sharia ruling</strong> is a Compliance reviewer's, whoever the work was for. Everything else is decided by the role its agent names.</p>
          ${p("Choose a decision, write at least a sentence, and record it. Nothing is chosen for you. Approving the board pack or a KYC case asks for a code sent to your email.")}
        `,
      },
      {
        id: "what-happens-next",
        title: "What happens after you decide",
        body: `
          <p>Approving a held pack sends it to its recipients. Your reason is kept for the audit trail and the next reviewer. A rejection stops any standing approval for that decision until the Supervisor turns it back on.</p>
        `,
      },
    ],
  },
  {
    id: "workforce",
    title: "Workforce",
    icon: "bot",
    topics: [
      {
        id: "roster",
        title: "Workforce",
        body: `
          <p>One card per agent: what it is for, when it runs, its last and next run, the law it checks against, whom it answers to, and how its recent runs went — completed unattended, sent to a person, stopped at the gate. An escalation is not a failure; it is the agent doing the thing it was built to do.</p>
          ${p("<strong>Run now</strong> starts a run today through the same gate a scheduled run passes. An event-triggered agent asks for the record to judge — a campaign asset, a KYC case.")}
        `,
      },
      {
        id: "agent-settings",
        title: "Agent settings",
        body: `
          <p>Under each card: when it runs (a frequency and a time, never a crontab), whether it runs at all, whom it answers to from the organisation directory, and which role signs each of its decisions.</p>
          ${p("A supervisor or an agent handler can change a schedule. Only the supervisor can change who signs.")}
        `,
      },
      {
        id: "run-detail",
        title: "Reading a run",
        body: `
          <p>Open any run and you see, in order: what the code computed; what it was checked against, by instrument, with whether GraphIQ answered live or an offline copy did; the rules in force with each verdict and citation; whether a model was called and what it was shown; and what a person decided, if anyone has.</p>
          ${p("<strong>Was this right?</strong> Two buttons let whoever read the output say so. That verdict counts against the agent's promotion — a decision a reader has marked wrong does not earn more autonomy until it is answered.")}
          ${p("A pack run can be downloaded as the PowerPoint deck the recipients received.")}
        `,
      },
    ],
  },
  {
    id: "health",
    title: "Runs and schedules",
    icon: "warn",
    topics: [
      {
        id: "what-broke",
        title: "What broke",
        body: `
          <p>Failed runs with the validator that failed, schedules with their last error, and agents not yet scheduled. A run stopped by a rule is not here — that is the gate working, and it is in someone's queue with its citation.</p>
        `,
      },
    ],
  },
  {
    id: "ledger",
    title: "Efficiency Ledger",
    icon: "governance",
    topics: [
      {
        id: "hours-returned",
        title: "Hours returned",
        body: `
          <p>Counted from the run records, one entry per run that produced something. A run stopped at the gate returns nothing — nobody was saved the work of a pack that was never written. A run a person had to sign returns the baseline minus the review time they declared, and it can be negative. An agent that costs more than it saves is a real result, not one to hide.</p>
        `,
      },
      {
        id: "baselines",
        title: "Baselines and where they come from",
        body: `
          <p>A baseline is what a piece of work took a person before the agent existed. It is a declaration by a named person with a stated basis — a time study, a manager's estimate — and every line shows who declared it, when, and on what.</p>
          ${p("A decision nobody has declared a baseline for reads <em>not measured</em>, never zero. The baseline is copied onto each entry when the run happens, so changing it later does not rewrite what last month returned.")}
        `,
      },
    ],
  },
  {
    id: "telemetry",
    title: "Autonomy",
    icon: "target",
    topics: [
      {
        id: "three-stages",
        title: "Three stages",
        body: `
          <p>Every decision an agent makes sits at one of three stages. <strong>Suggest</strong>: the system proposes, a person acts. <strong>Approve</strong>: the system does the work and holds it for a named role to sign. <strong>Autonomous</strong>: the system acts and audits are sampled.</p>
          ${p("The stage governs what happens to what a run produced. It never governs the gate: a blocking rule stops a run at every stage, and no stage lets a model decide anything. Anything customer-facing or regulator-facing is approved by a person every time and cannot be made autonomous.")}
        `,
      },
      {
        id: "earning-rope",
        title: "How a decision earns more rope",
        body: `
          <p>Each card shows the record behind the stage — runs, decisions, what readers said — and a readiness line in words: what is still missing, never a score. When the record supports the next stage the page recommends the move; on the first evidence against a stage above the first, it recommends pulling back. Trust already given is the trust that can do damage.</p>
          ${p("Accepting a recommendation fills the control in with the evidence as the reason. It does not move anything. The supervisor moves it, with a reason, and the move is recorded.")}
        `,
      },
    ],
  },
  {
    id: "regulatory",
    title: "Regulatory memory",
    icon: "star",
    topics: [
      {
        id: "lookup",
        title: "A lookup, not a chat",
        body: `
          <p>Ask which instrument was in force on a date, or which rules apply, and the answer carries its citation and pack version. Nothing on this page is generated.</p>
          ${p("Rules are resolved as at the date of the event being judged, so a run dated before a law changed is judged by the law that applied then, and says so.")}
        `,
      },
      {
        id: "placeholders",
        title: "Obligations and placeholders",
        body: `
          <p>Some rules are statute, regulator rules or Sharia rulings: these may block a run. Others are Xamun placeholders awaiting curation — a draft of a rule whose source has not yet been read. A placeholder may warn and may never block, and every screen that shows a rule says which kind it is.</p>
        `,
      },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    icon: "settings",
    topics: [
      {
        id: "organisation",
        title: "Organisation",
        body: `
          <p>The people this department reports to and works for: a name, a title, an address and who they report to. They need no login. An agent answers to someone here, and a report goes to someone here.</p>
        `,
      },
      {
        id: "distribution",
        title: "Report distribution",
        body: `
          <p>Who receives each report. An address on a list gets the pack as a PowerPoint attachment with a short brief in the body. Until someone is named for a report, it goes to the workspace's supervisors.</p>
        `,
      },
      {
        id: "outbound",
        title: "Outbound email",
        body: `
          <p>The name a recipient sees in the From line and the address a reply goes to. The sending address itself belongs to the mail account the deployment authenticates with and is set alongside those credentials, not here. <strong>Send a test</strong> shows you exactly what a recipient will see.</p>
        `,
      },
      {
        id: "notifications",
        title: "Your notifications",
        body: `
          <p>Whether you hear about a decision waiting on you or an agent failure as it happens, and whether you also get a daily or weekly digest. These are yours; they do not change what anyone else receives.</p>
        `,
      },
    ],
  },
  {
    id: "studio",
    title: "Template studio",
    icon: "factory",
    topics: [
      {
        id: "versions",
        title: "Versions, evals and Approve",
        body: `
          ${p("Every agent is listed with its version, whether it is approved, a draft or retired, and its last eval report. <strong>Evals out of date</strong> means the rules changed since its evals ran: it keeps running, but a new version can't be approved until its evals pass again.")}
          ${p("<strong>New draft</strong> copies the running version. <strong>Run evals</strong> shows four checks in words. <strong>Not enough history yet</strong> is not a failure: the check needs five recorded runs, which the schedule or a request makes.")}
        `,
      },
      {
        id: "policies",
        title: "Standing approvals and codes",
        body: `
          ${p("For each decision: tick <strong>Apply my earlier approval when the figures match</strong>, and choose how many earlier approvals must match, how recent they must be, and which figures are compared. A matching run is then released by code, and the thread says so. The first rejection or <em>It was wrong</em> suspends it; saving with it on resumes it. It is never offered for a decision that always needs a person or a code.")}
        `,
      },
    ],
  },
  {
    id: "threads",
    title: "Threads",
    icon: "msg",
    topics: [
      {
        id: "reading-threads",
        title: "Reading someone's thread",
        body: `
          ${p("Supervisors and Compliance reviewers can read what anyone asked and was told, read-only. Pick a person, narrow it to one agent or a span of days, and export it as a CSV exactly as shown. Decisions are marked.")}
        `,
      },
    ],
  },
  {
    id: "systems",
    title: "Systems",
    icon: "flow",
    topics: [
      {
        id: "systems-list",
        title: "What the agents reach",
        body: `
          ${p("Each system the agents read from or write to, how it is signed in to — a service account, or your own single sign-on — and whether it is connected or still planned. The <strong>XI socket</strong> line says whether anything connects out to Xamun; it is off.")}
        `,
      },
    ],
  },
  {
    id: "faq",
    title: "FAQ",
    icon: "help",
    topics: [
      {
        id: "no-chat",
        title: "Can I ask the agent anything?",
        body: `
          <p>You can ask for any job you're allowed, in your own words, and you can ask questions about your own records. A question is answered by a model that can only use <strong>read-only</strong> lookups over your records: it cannot start, send, approve or change anything, and every figure in its answer is <strong>computed by code</strong> and put there by code. If it can't answer reliably, it says so and offers the nearest jobs.</p>
        `,
      },
      {
        id: "wrong-output",
        title: "An agent produced something wrong",
        body: `
          <p>Open the run and press <strong>It was wrong</strong>, with a note if you have one. It counts on the telemetry immediately, and a decision with a thumbs-down against it will not be recommended for more autonomy until it is answered. If the pack was held for sign-off, reject it with your reason; nothing goes out.</p>
        `,
      },
      {
        id: "stop-an-agent",
        title: "Can I stop an agent?",
        body: `
          <p>Yes. Open its settings on the roster and switch it off. It stops being scheduled; its record stays. A supervisor can also pull any decision back to <em>approve</em> or <em>suggest</em> on the telemetry page, with a reason, and that is recorded too.</p>
        `,
      },
      {
        id: "demo-data",
        title: "Is this real data?",
        body: `
          <p>Every screen says <em>Synthetic demonstration data</em> while it is. The book, the cases, the campaign assets, the digital funnel and the vendor tickets are generated. The rules are real drafts of real instruments and are marked where they are placeholders.</p>
        `,
      },
    ],
  },
];

/**
 * Which section the drawer opens on, from where the person is standing.
 * Help that opens on "Welcome" when you are looking at the queue is help
 * that has not noticed you.
 */
export function getDefaultSection(pathname: string): string {
  if (pathname.startsWith("/app")) return "app";
  if (pathname.includes("/dashboard")) return "home";
  if (pathname.includes("/operator")) return "queue";
  if (pathname.includes("/workforce")) return "workforce";
  if (pathname.includes("/agent-health")) return "health";
  if (pathname.includes("/ledger") || pathname.includes("/outcomes")) return "ledger";
  if (pathname.includes("/autonomy")) return "telemetry";
  if (pathname.includes("/regulatory")) return "regulatory";
  if (pathname.includes("/settings")) return "settings";
  if (pathname.includes("/templates")) return "studio";
  if (pathname.includes("/threads")) return "threads";
  if (pathname.includes("/systems")) return "systems";
  return "welcome";
}

export function getDefaultTopic(pathname: string): string {
  if (pathname.startsWith("/app")) {
    if (pathname.includes("/runs/")) return "checking-a-result";
    if (pathname.includes("/queue")) return "approving";
    return "using-the-chat";
  }
  if (pathname.includes("/workforce/runs/")) return "run-detail";
  if (pathname.includes("/dashboard")) return "home-reads";
  if (pathname.includes("/operator")) return "what-a-fork-is";
  if (pathname.includes("/workforce")) return "roster";
  if (pathname.includes("/agent-health")) return "what-broke";
  if (pathname.includes("/ledger") || pathname.includes("/outcomes")) return "hours-returned";
  if (pathname.includes("/autonomy")) return "three-stages";
  if (pathname.includes("/regulatory")) return "lookup";
  if (pathname.includes("/settings")) return "organisation";
  if (pathname.includes("/templates")) return "versions";
  if (pathname.includes("/threads")) return "reading-threads";
  if (pathname.includes("/systems")) return "systems-list";
  return "what-this-is";
}
